'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Copy01Icon,
  LinkSquare02Icon,
} from '@terra/ui-icons';
import { useState, useMemo, useCallback } from 'react';
import { IconButton } from '../../../../../components/icon-button';
import { useTranslations } from 'next-intl';
import { z } from 'zod';

type InteractshRendererProps = {
  data: unknown;
};
// Zod schemas for proper validation
const InteractionSchema = z.object({
  protocol: z.string().optional(),
  timestamp: z.string().optional(),
  remoteAddress: z.string().optional(),
  rawRequest: z.string().optional(),
  rawResponse: z.string().optional(),
  uniqueId: z.string().optional(),
  fullPath: z.string().optional(),
  qType: z.string().optional(),
  // Support kebab-case variants
  'remote-address': z.string().optional(),
  'raw-request': z.string().optional(),
  'raw-response': z.string().optional(),
  'unique-id': z.string().optional(),
  'full-path': z.string().optional(),
  'q-type': z.string().optional(),
});
const InteractshDataSchema = z.object({
  mode: z
    .enum(['domain-generated', 'interactions-check', 'dynamic-response'])
    .optional(),
  url: z.string().optional(),
  domain: z.string().optional(),
  dynamicUrl: z.string().optional(),
  sessionId: z.string().optional(),
  status: z.string().optional(),
  message: z.string().optional(),
  count: z.number().optional(),
  interactions: z.array(InteractionSchema).optional(),
  rawResult: z.string().optional(),
});

type ParsedInteraction = {
  protocol?: string;
  timestamp?: string;
  remoteAddress?: string;
  rawRequest?: string;
  rawResponse?: string;
  uniqueId?: string;
  fullPath?: string;
  qType?: string;
};
type InteractshMode =
  | 'domain-generated'
  | 'interactions-check'
  | 'dynamic-response'
  | 'unknown';
type ParsedInteractshData = {
  mode: InteractshMode;
  url?: string;
  dynamicUrl?: string;
  sessionId?: string;
  status?: string;
  message?: string;
  count?: number;
  interactions?: ParsedInteraction[];
  rawResult?: string;
};
const MODE_INFERENCES: Array<{
  test: (obj: z.infer<typeof InteractshDataSchema>) => boolean;
  mode: InteractshMode;
}> = [
  { test: (o) => Boolean(o.dynamicUrl), mode: 'dynamic-response' },
  { test: (o) => Boolean(o.url || o.domain), mode: 'domain-generated' },
  { test: (o) => o.interactions !== undefined, mode: 'interactions-check' },
];
const inferInteractshMode = (
  obj: z.infer<typeof InteractshDataSchema>,
): InteractshMode =>
  MODE_INFERENCES.find((i) => i.test(obj))?.mode ?? 'unknown';

function parseInteractshData(data: unknown): ParsedInteractshData {
  const result = InteractshDataSchema.safeParse(data);

  if (!result.success) {
    return { mode: 'unknown' };
  }

  const obj = result.data;
  const mode: InteractshMode = obj.mode ?? inferInteractshMode(obj);
  // Normalize interactions (handle kebab-case to camelCase)
  const interactions = obj.interactions?.map((i) => ({
    protocol: i.protocol,
    timestamp: i.timestamp,
    remoteAddress: i.remoteAddress ?? i['remote-address'],
    rawRequest: i.rawRequest ?? i['raw-request'],
    rawResponse: i.rawResponse ?? i['raw-response'],
    uniqueId: i.uniqueId ?? i['unique-id'],
    fullPath: i.fullPath ?? i['full-path'],
    qType: i.qType ?? i['q-type'],
  }));

  return {
    mode,
    url: obj.url ?? obj.domain,
    dynamicUrl: obj.dynamicUrl,
    sessionId: obj.sessionId,
    status: obj.status,
    message: obj.message,
    count: obj.count ?? interactions?.length,
    interactions,
    rawResult: obj.rawResult,
  };
}

// ============================================================================
// Shared flat presentation helpers
// ============================================================================
function CopyIconButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <IconButton
      variant="ghost"
      size="xs"
      aria-label={label}
      onClick={handleCopy}
      flexShrink={0}
    >
      {copied ? <Tick01Icon size={12} /> : <Copy01Icon size={12} />}
    </IconButton>
  );
}

function UrlRow({ url, showOpen }: { url: string; showOpen?: boolean }) {
  const handleOpenInBrowser = useCallback(() => {
    const fullUrl =
      url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;

    window.open(fullUrl, '_blank');
  }, [url]);

  return (
    <HStack gap={1} alignItems="center" width="100%">
      <Text
        fontSize="xs"
        fontFamily="mono"
        color="fg.muted"
        wordBreak="break-all"
        flex={1}
        minW={0}
      >
        {url}
      </Text>
      <CopyIconButton value={url} label="Copy URL" />
      {showOpen && (
        <IconButton
          variant="ghost"
          size="xs"
          aria-label="Open in browser"
          onClick={handleOpenInBrowser}
          flexShrink={0}
        >
          <LinkSquare02Icon size={12} />
        </IconButton>
      )}
    </HStack>
  );
}

// ============================================================================
// Mode 1: Domain Generated View (simple URL display)
// ============================================================================
function DomainGeneratedView({ url }: { url: string }) {
  return <UrlRow url={url} showOpen />;
}

// ============================================================================
// Mode 3: Dynamic Response View
// ============================================================================
function DynamicResponseView({ dynamicUrl }: { dynamicUrl: string }) {
  return <UrlRow url={dynamicUrl} />;
}

// ============================================================================
// Mode 2: Interactions Check View (flat expandable rows)
// ============================================================================
function InteractionRow({
  interaction,
  isExpanded,
  onToggle,
}: {
  interaction: ParsedInteraction;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('designSystem.chat.interactsh');
  const {
    protocol,
    timestamp,
    remoteAddress,
    rawRequest,
    rawResponse,
    uniqueId,
    fullPath,
    qType,
  } = interaction;
  const formattedTimestamp = useMemo(() => {
    if (!timestamp) return null;

    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  }, [timestamp]);
  const hasDetails =
    rawRequest || rawResponse || remoteAddress || uniqueId || fullPath || qType;

  return (
    <VStack alignItems="stretch" gap={1} width="100%">
      <HStack
        gap={1.5}
        cursor="pointer"
        onClick={onToggle}
        width="100%"
        alignItems="center"
      >
        <Box color="fg.muted" display="flex" alignItems="center" flexShrink={0}>
          {isExpanded ? (
            <ArrowDown01Icon size={12} />
          ) : (
            <ArrowRight01Icon size={12} />
          )}
        </Box>
        {protocol && (
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.default"
            flexShrink={0}
          >
            {protocol.toUpperCase()}
          </Text>
        )}
        {qType && (
          <Text fontSize="xs" color="fg.muted" flexShrink={0}>
            {qType}
          </Text>
        )}
        {formattedTimestamp && (
          <Text fontSize="xs" color="fg.muted" fontFamily="mono">
            {formattedTimestamp}
          </Text>
        )}
        <Box flex={1} />
        {remoteAddress && (
          <Text
            fontSize="xs"
            color="fg.subtle"
            fontFamily="mono"
            flexShrink={0}
          >
            {remoteAddress}
          </Text>
        )}
      </HStack>

      {isExpanded && (
        <Box
          borderLeftWidth="2px"
          borderLeftColor="border.default"
          pl={2}
          width="100%"
        >
          <VStack alignItems="stretch" gap={2} width="100%">
            {uniqueId && (
              <Text fontSize="xs" fontFamily="mono" color="fg.muted">
                {t('id')} {uniqueId}
              </Text>
            )}
            {fullPath && (
              <Text
                fontSize="xs"
                fontFamily="mono"
                color="fg.muted"
                wordBreak="break-all"
              >
                {t('path')} {fullPath}
              </Text>
            )}
            {rawRequest && (
              <VStack alignItems="stretch" gap={1} width="100%">
                <Text fontSize="xs" color="fg.muted">
                  {t('request')}
                </Text>
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="fg.subtle"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                >
                  {rawRequest}
                </Text>
              </VStack>
            )}
            {rawResponse && (
              <VStack alignItems="stretch" gap={1} width="100%">
                <Text fontSize="xs" color="fg.muted">
                  {t('response')}
                </Text>
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="fg.subtle"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                >
                  {rawResponse}
                </Text>
              </VStack>
            )}
            {!hasDetails && (
              <Text fontSize="xs" color="fg.muted">
                {t('noDetails')}
              </Text>
            )}
          </VStack>
        </Box>
      )}
    </VStack>
  );
}

function InteractionsCheckView({
  interactions,
  message,
}: {
  interactions?: ParsedInteraction[];
  message?: string;
}) {
  const t = useTranslations('designSystem.chat.interactsh');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const hasInteractions = interactions && interactions.length > 0;

  if (!hasInteractions) {
    return (
      <VStack gap={0.5} width="100%" alignItems="stretch">
        <Text fontSize="xs" color="fg.muted">
          {t('noInteractions')}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {message || t('interactionsHint')}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack gap={2} width="100%" alignItems="stretch">
      {interactions.map((interaction, index) => (
        <InteractionRow
          key={index}
          interaction={interaction}
          isExpanded={expandedIndex === index}
          onToggle={() =>
            setExpandedIndex(expandedIndex === index ? null : index)
          }
        />
      ))}
    </VStack>
  );
}

// ============================================================================
// Main Component
// ============================================================================
export function InteractshRenderer({ data }: InteractshRendererProps) {
  const parsed = useMemo(() => parseInteractshData(data), [data]);

  if (parsed.mode === 'domain-generated' && parsed.url) {
    return <DomainGeneratedView url={parsed.url} />;
  }

  if (parsed.mode === 'dynamic-response' && parsed.dynamicUrl) {
    return <DynamicResponseView dynamicUrl={parsed.dynamicUrl} />;
  }

  if (parsed.mode === 'interactions-check') {
    return (
      <InteractionsCheckView
        interactions={parsed.interactions}
        message={parsed.message}
      />
    );
  }

  if (parsed.mode === 'unknown' && parsed.rawResult) {
    return (
      <Text
        fontSize="xs"
        fontFamily="mono"
        color="fg.muted"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
      >
        {parsed.rawResult}
      </Text>
    );
  }

  if (typeof data === 'string' && data.trim()) {
    return (
      <Text
        fontSize="xs"
        fontFamily="mono"
        color="fg.muted"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
      >
        {data}
      </Text>
    );
  }

  return null;
}
